import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Package,
  ShoppingCart,
  DollarSign,
  Clock,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

interface PartsForecast {
  id: string;
  item_name: string;
  current_stock: number;
  avg_monthly_usage: number;
  forecast_demand: number;
  weeks_until_stockout?: number;
  reorder_recommended: boolean;
  recommended_quantity: number;
  estimated_cost: number;
  seasonal_factor?: number;
}

interface ForecastList {
  data: PartsForecast[];
  total: number;
}

interface ForecastSummary {
  total_items_tracked: number;
  reorder_count: number;
  estimated_monthly_spend: number;
  weeks_until_min_stockout: number | null;
  seasonal_notes: string[];
}

const MOCK_SUMMARY: ForecastSummary = {
  total_items_tracked: 42,
  reorder_count: 8,
  estimated_monthly_spend: 3420.0,
  weeks_until_min_stockout: 3,
  seasonal_notes: [],
};

const MOCK_LIST: ForecastList = {
  data: [],
  total: 0,
};

export default function PartsForecastPage() {
  const [filterReorder, setFilterReorder] = useState(false);
  const [generating, setGenerating] = useState(false);

  const { data: summary, isLoading: summaryLoading } = useApi<ForecastSummary>('/api/parts-forecast/summary');
  const { data: listData, isLoading: listLoading, refetch } = useApi<ForecastList>(
    `/api/parts-forecast${filterReorder ? '?reorder_recommended=true' : ''}`,
  );

  const s = summary ?? MOCK_SUMMARY;
  const items = listData?.data ?? MOCK_LIST.data;

  async function handleGenerate() {
    setGenerating(true);
    try {
      await fetch('/api/parts-forecast', { method: 'POST' });
      refetch?.();
    } finally {
      setGenerating(false);
    }
  }

  const stockoutColor = s.weeks_until_min_stockout !== null && s.weeks_until_min_stockout <= 2
    ? '#EF4444'
    : s.weeks_until_min_stockout !== null && s.weeks_until_min_stockout <= 4
      ? '#F97316'
      : '#22C55E';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Parts Procurement Intelligence"
        description="Demand forecasting and reorder recommendations"
        actions={
          <div className="flex items-center gap-2">
            {(summaryLoading || listLoading) && <Spinner size={16} />}
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ background: 'var(--primary)', color: '#fff', opacity: generating ? 0.6 : 1 }}
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating…' : 'Run Forecast'}
            </button>
          </div>
        }
      />

      {/* Seasonal Notes */}
      {s.seasonal_notes.length > 0 && (
        <div
          className="flex items-start gap-3 p-3 rounded-xl text-sm"
          style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', color: '#EAB308' }}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{s.seasonal_notes.join(' | ')}</span>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Items Tracked" value={s.total_items_tracked} />
        <StatCard icon={ShoppingCart} label="Reorder Needed" value={s.reorder_count} accent="warning" />
        <StatCard
          icon={DollarSign}
          label="Est Monthly Spend"
          value={`$${s.estimated_monthly_spend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
        />
        <div
          className="rounded-xl p-4 flex flex-col gap-1"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
              Weeks Until Stockout
            </span>
          </div>
          <span className="text-2xl font-bold" style={{ color: stockoutColor }}>
            {s.weeks_until_min_stockout !== null ? s.weeks_until_min_stockout : '—'}
          </span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>lowest across fleet</span>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div
            className="flex items-center justify-between p-4 border-b"
            style={{ borderColor: 'var(--glass-border)' }}
          >
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Parts Forecast
            </h2>
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--muted-foreground)' }}>
              <input
                type="checkbox"
                checked={filterReorder}
                onChange={e => setFilterReorder(e.target.checked)}
                className="rounded"
              />
              Reorder only
            </label>
          </div>

          {listLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size={24} />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2" style={{ color: 'var(--muted-foreground)' }}>
              <Package className="w-8 h-8 opacity-40" />
              <p className="text-sm">No forecasts available — run a forecast to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    {['Item', 'Current Stock', 'Monthly Usage', 'Forecast Demand', 'Weeks to Stockout', 'Reorder?', 'Recommended Qty', 'Est Cost'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const isReorder = item.reorder_recommended;
                    const rowStyle = isReorder
                      ? { background: 'rgba(249,115,22,0.06)', borderBottom: '1px solid var(--glass-border)' }
                      : { borderBottom: '1px solid var(--glass-border)' };
                    const weeksColor =
                      item.weeks_until_stockout !== undefined && item.weeks_until_stockout <= 2
                        ? '#EF4444'
                        : item.weeks_until_stockout !== undefined && item.weeks_until_stockout <= 4
                          ? '#F97316'
                          : 'var(--foreground)';

                    return (
                      <tr key={item.id} style={rowStyle}>
                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>
                          {item.item_name}
                        </td>
                        <td className="px-4 py-3 font-mono text-right" style={{ color: 'var(--foreground)' }}>
                          {item.current_stock}
                        </td>
                        <td className="px-4 py-3 font-mono text-right" style={{ color: 'var(--muted-foreground)' }}>
                          {item.avg_monthly_usage.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 font-mono text-right" style={{ color: 'var(--foreground)' }}>
                          {item.forecast_demand}
                        </td>
                        <td className="px-4 py-3 font-mono text-right" style={{ color: weeksColor }}>
                          {item.weeks_until_stockout !== undefined ? item.weeks_until_stockout : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {isReorder ? (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}
                            >
                              Yes
                            </span>
                          ) : (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}
                            >
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-right" style={{ color: 'var(--foreground)' }}>
                          {item.recommended_quantity}
                        </td>
                        <td className="px-4 py-3 font-mono text-right" style={{ color: 'var(--foreground)' }}>
                          ${item.estimated_cost.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
